/**
 * Audit Trail Service & Automated Logger
 * Automatically records and provides audit trails for user logins, logouts, 
 * session durations, and significant portal actions across all modules.
 */

const AUDIT_LOGS_KEY = "ouhs_audit_trails_logs";
const SESSION_LOGS_KEY = "ouhs_session_trails_logs";

/**
 * Log a new audit event automatically
 */
export const logAuditEvent = (action, module, details, userObj = null) => {
  try {
    const currentUser = userObj || JSON.parse(localStorage.getItem("user") || "{}");
    const newLog = {
      id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      user_id: currentUser.id || currentUser.email || "system",
      user_name: currentUser.name || currentUser.display_name || currentUser.email || "System User",
      role: currentUser.role || currentUser.user_type || "User",
      college: currentUser.college || currentUser.institution || "OUHS Affiliated Institution",
      action: action.toUpperCase(), // CREATE, VIEW, UPDATE, DELETE, DOWNLOAD, UPLOAD, APPROVE, REJECT, LOGIN, LOGOUT
      module: module,
      details: details,
      timestamp: new Date().toISOString(),
      ip_address: "192.168.1.102",
      device: navigator.userAgent.includes("Mobile") ? "Mobile Device (Chrome/Safari)" : "Desktop Workstation (Windows/Chrome)",
    };

    const existingLogs = getAuditLogs();
    existingLogs.unshift(newLog);
    // Keep max 500 records
    localStorage.setItem(AUDIT_LOGS_KEY, JSON.stringify(existingLogs.slice(0, 500)));
    return newLog;
  } catch (err) {
    console.error("Failed to log audit event:", err);
  }
};

/**
 * Record user login session start
 */
export const logUserLogin = (userObj) => {
  try {
    const session = {
      id: `sess_${Date.now()}`,
      user_id: userObj.id || userObj.email,
      user_name: userObj.name || userObj.email,
      role: userObj.role || "User",
      college: userObj.college || "OUHS Affiliated Institution",
      login_time: new Date().toISOString(),
      logout_time: null,
      duration: "Active Session",
      ip_address: "192.168.1.102",
      device: navigator.userAgent.includes("Mobile") ? "Mobile Browser" : "Desktop Chrome",
    };

    localStorage.setItem("ouhs_current_session", JSON.stringify(session));

    const sessionLogs = getSessionLogs();
    sessionLogs.unshift(session);
    localStorage.setItem(SESSION_LOGS_KEY, JSON.stringify(sessionLogs.slice(0, 200)));

    logAuditEvent("LOGIN", "Authentication", `User ${userObj.name || userObj.email} logged in successfully`, userObj);
  } catch (err) {
    console.error("Error logging login:", err);
  }
};

/**
 * Record user logout session end
 */
export const logUserLogout = () => {
  try {
    const currentSessStr = localStorage.getItem("ouhs_current_session");
    if (!currentSessStr) return;

    const currentSess = JSON.parse(currentSessStr);
    const logoutTime = new Date();
    const loginTime = new Date(currentSess.login_time);

    const diffMs = logoutTime - loginTime;
    const mins = Math.floor(diffMs / 60000);
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;

    const durationStr = hrs > 0 ? `${hrs}h ${remMins}m` : `${mins || 1} mins`;

    currentSess.logout_time = logoutTime.toISOString();
    currentSess.duration = durationStr;

    const sessionLogs = getSessionLogs();
    const idx = sessionLogs.findIndex((s) => s.id === currentSess.id);
    if (idx !== -1) {
      sessionLogs[idx] = currentSess;
      localStorage.setItem(SESSION_LOGS_KEY, JSON.stringify(sessionLogs));
    }

    logAuditEvent("LOGOUT", "Authentication", `User session ended (Duration: ${durationStr})`, currentSess);
    localStorage.removeItem("ouhs_current_session");
  } catch (err) {
    console.error("Error logging logout:", err);
  }
};

/**
 * Helper to check if two college strings match (case-insensitive & normalized)
 */
export const isSameCollegeName = (col1, col2) => {
  if (!col1 || !col2) return false;
  const c1 = String(col1).toLowerCase().replace(/[^a-z0-9]/g, "");
  const c2 = String(col2).toLowerCase().replace(/[^a-z0-9]/g, "");
  return c1 === c2 || c1.includes(c2) || c2.includes(c1);
};

/**
 * Get all stored audit logs with fallback to mock data
 */
export const getAuditLogs = () => {
  try {
    const logsStr = localStorage.getItem(AUDIT_LOGS_KEY);
    if (logsStr) {
      return JSON.parse(logsStr);
    }
  } catch (e) {
    console.error("Error parsing audit logs:", e);
  }
  const defaultLogs = generateInitialAuditLogs();
  localStorage.setItem(AUDIT_LOGS_KEY, JSON.stringify(defaultLogs));
  return defaultLogs;
};

/**
 * Get audit logs strictly isolated by college name
 */
export const getAuditLogsByCollege = (collegeName) => {
  const allLogs = getAuditLogs();
  if (!collegeName) return allLogs;
  return allLogs.filter((log) => isSameCollegeName(log.college, collegeName));
};

/**
 * Get all stored session logs with fallback to mock data
 */
export const getSessionLogs = () => {
  try {
    const logsStr = localStorage.setItem ? localStorage.getItem(SESSION_LOGS_KEY) : null;
    if (logsStr) {
      return JSON.parse(logsStr);
    }
  } catch (e) {
    console.error("Error parsing session logs:", e);
  }
  const defaultSessions = generateInitialSessionLogs();
  localStorage.setItem(SESSION_LOGS_KEY, JSON.stringify(defaultSessions));
  return defaultSessions;
};

/**
 * Get session logs strictly isolated by college name
 */
export const getSessionLogsByCollege = (collegeName) => {
  const allSessions = getSessionLogs();
  if (!collegeName) return allSessions;
  return allSessions.filter((sess) => isSameCollegeName(sess.college, collegeName));
};

/**
 * Helper to generate pre-populated realistic audit logs for initial view
 */
function generateInitialAuditLogs() {
  return [];
}

function generateInitialSessionLogs() {
  return [];
}
